import { Container as ContainerInversify } from 'inversify';
import { types } from "@studyportals/fawkes/organisations-search-seo";
import { Newable } from './Newable';
import { Dependencies } from './Dependencies';
import { Cache } from '../Cache';
import { EventAggregationService } from '../EventAggregationService';
import { FilterProvider } from '@/filters/providers/FilterProvider';
import { LocationFilterProvider } from '@/filters/providers/LocationFilterProvider';
import { FilterOptions } from '@/filters/options/FilterOptions';
import { FilterSelection } from '@/filters/selection/FilterSelection';
import { Routing } from '@/router/Routing';
import { UniversityFilterProvider } from '@/filters/providers/UniversityFilterProvider';
import { FilterConfig } from '@/filters/providers/FilterConfig';
import { DomainApiProjectionOrganisationForSearch } from '@/infrastructure/DomainApiProjectionOrganisationForSearch';
import { ErrorReporter } from '@/infrastructure/ErrorReporter';
import { RouteBuilder } from '@/router/RouteBuilder';
import { Url } from '@/url/Url';
import { Store } from '@/store/Store';
import { EmbeddedStateFactory } from '@/url/EmbeddedStateFactory';
import { QueryParameterSelection } from '@/url/QueryParameterSelection';
import { SearchOrganisationPresenter } from '@/presenters/SearchOrganisationPresenter';
import { RankingOrganisationPresenter } from '@/presenters/RankingOrganisationPresenter';
import { Search } from '../Search';
import { ApplicationStatus } from '../ApplicationStatus';
import { FilterCounter } from '@/filters/selection/FilterCounter';
import { SeoMetaDataFilterVariations } from '@/seo/meta-data/SeoMetaDataFilterVariations';
import { SeoHeading } from '@/seo/heading/SeoHeading';
import { SeoHeadingFilterVariations } from '@/seo/heading/SeoHeadingFilterVariations';
import { SeoMetaData } from '@/seo/meta-data/SeoMetaData';
import { CanonicalUrl } from '@/seo/CanonicalUrl';
import { Prerender } from '@/seo/Prerender';
import { ImpressionTracking } from '@/tracking/ImpressionTracking';
import { Tracking } from '@/tracking/Tracking';
import { ClickTracking } from '@/tracking/ClickTracking';
import { SortingSelection } from '@/sorting/SortingSelection';
import { SortingState } from '@/sorting/SortingState';

import { SeoInfoBase } from '@/seo/SeoInfoBase';
import { Indexability } from '@/seo/Indexability';
import { IndexableUrl } from '@/url/IndexableUrl';
import { SeoMetaDataPrerender } from '@/seo/meta-data/SeoMetaDataPrerender';
import { LatencyTracking } from '@/tracking/LatencyTracking';
import { StaticContentBuilder } from '@/faq/StaticContentBuilder';
import { SeoIndexabilityInfoBase } from '@/seo/SeoIndexabilityInfoBase';
import { FilterSelectionForIndex } from '@/filters/selection/FilterSelectionForIndex';
import { SeoApplicationState } from '@/seo/SeoApplicationState';
import { SeoSortingSelection } from '@/seo/SeoSortingSelection';


export class Container {
	public static readonly injectionKey = Symbol('Container');

	private readonly container: ContainerInversify = new ContainerInversify();

	public resolve<T>(constructor: Newable<T>): T {
		this.bindMissingTopLevelDependency<T>(constructor);

		return this.container.get<T>(constructor);
	}

	public static configure(dependencies: Dependencies = {}): Container {
		const result = new Container();

		result.container.bind(Window).toConstantValue(dependencies.window ?? window);
		result.container.bind(ErrorReporter).toConstantValue(ErrorReporter.instance);
		result.container.bind(Cache).toSelf().inSingletonScope();
		result.container.bind(Routing).toSelf().inSingletonScope();
		result.container.bind(RouteBuilder).toSelf().inSingletonScope();
		result.container.bind(FilterConfig).toSelf().inSingletonScope();
		result.container.bind(Url).toSelf().inSingletonScope();
		result.container.bind(EmbeddedStateFactory).toSelf().inSingletonScope();
		result.container.bind(SearchOrganisationPresenter).toSelf().inSingletonScope();
		result.container.bind(RankingOrganisationPresenter).toSelf().inSingletonScope();
		result.container.bind(Search).toSelf().inSingletonScope();
		result.container.bind(ApplicationStatus).toSelf().inSingletonScope();
		result.container.bind(FilterCounter).toSelf().inSingletonScope();
		result.container.bind(SeoHeadingFilterVariations).toSelf().inSingletonScope();
		result.container.bind(SeoMetaDataFilterVariations).toSelf().inSingletonScope();
		result.container.bind(SeoHeading).toSelf().inSingletonScope();
		result.container.bind(SeoMetaData).toSelf().inSingletonScope();
		result.container.bind(SeoMetaDataPrerender).toSelf().inSingletonScope();
		result.container.bind(SeoInfoBase).toSelf().inSingletonScope();
		result.container.bind(QueryParameterSelection).toSelf().inSingletonScope();
		result.container.bind(types.SeoSortingState).to(SeoSortingSelection).inSingletonScope();
		result.container.bind(types.SeoInfoBase).to(SeoIndexabilityInfoBase).inSingletonScope();
		result.container.bind(types.SeoFilterState).to(FilterSelectionForIndex).inSingletonScope();
		result.container.bind(types.SeoApplicationState).to(SeoApplicationState).inSingletonScope();

		result.container.bind(CanonicalUrl).toSelf().inSingletonScope();
		result.container.bind(Prerender).toSelf().inSingletonScope();
		result.container.bind(ImpressionTracking).toSelf().inSingletonScope();
		result.container.bind(ClickTracking).toSelf().inSingletonScope();
		result.container.bind(LatencyTracking).toSelf().inSingletonScope();
		result.container.bind(Tracking).toSelf().inSingletonScope();
		result.container.bind(Indexability).toSelf().inSingletonScope();
		result.container.bind(IndexableUrl).toSelf().inSingletonScope();
		result.container.bind(StaticContentBuilder).toSelf().inSingletonScope();

		result.bindToSelfOrValue(FilterSelection, dependencies.filterSelection);
		result.bindToSelfOrValue(SortingSelection, dependencies.sortingSelection);
		result.bindToSelfOrValue(SortingState, dependencies.sortingState);
		result.bindToSelfOrValue(EventAggregationService, dependencies.eventAggregationService);
		result.bindToSelfOrValue<FilterProvider>(FilterProvider, dependencies.filterProvider);
		result.bindToSelfOrValue<LocationFilterProvider>(LocationFilterProvider, dependencies.locationFilterProvider);
		result.bindToSelfOrValue<UniversityFilterProvider>(UniversityFilterProvider, dependencies.universityFilterProvider);
		result.bindToSelfOrValue<FilterOptions>(FilterOptions, dependencies.filterOptions);
		result.bindToSelfOrValue(
			DomainApiProjectionOrganisationForSearch,
			dependencies.domainApiProjectionOrganisationForSearch
		);
		result.bindToSelfOrValue(Store, dependencies.store);

		result.instantiateActiveInstances();
		return result;
	}

	private instantiateActiveInstances(): void {
		this.container.resolve(SeoHeading);
		this.container.resolve(SeoMetaData);
		this.container.resolve(FilterSelection);
		this.container.resolve(EventAggregationService);
		this.container.resolve(Cache);
		this.container.resolve(Prerender);
		this.container.resolve(Url);
		this.container.resolve(Indexability);
		this.container.resolve(ApplicationStatus);
		this.container.resolve(Search);
	}

	private bindMissingTopLevelDependency<T>(constructor: Newable<T>): void {
		if (this.container.isBound(constructor)) {
			return;
		}

		this.container.bind(constructor).toSelf();
	}

	private bindToSelfOrValue<T>(constructor: Newable<T>, value: T | undefined): void {
		if (value) {
			this.container.bind(constructor).toConstantValue(value);
		} else {
			this.container.bind(constructor).toSelf().inSingletonScope();
		}
	}
}
